import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';

import { usePosContext } from '../PosContext';

interface AddDiscoutDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddDiscoutDialog = ({ open, onClose }: AddDiscoutDialogProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.cart.addDiscountDialog',
  });

  const [discountCode, setDiscountCode] = useState('');

  const {
    applyDiscount,
    isLoadingDiscount,
    isLoadingIntent,
    isErrorDiscount,
    cartSummary: { discount: totalDiscount },
  } = usePosContext();

  const prevTotalDiscount = useRef(totalDiscount);

  useEffect(() => {
    if (
      !isLoadingDiscount &&
      !isErrorDiscount &&
      totalDiscount > 0 &&
      prevTotalDiscount.current !== totalDiscount
    ) {
      onClose();
      setDiscountCode('');
    }
    prevTotalDiscount.current = totalDiscount;
  }, [totalDiscount, isLoadingDiscount, isErrorDiscount, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      maxWidth="sm"
      fullWidth
    >
      <Stack spacing={2}>
        <TextField
          label={t('discountCode')}
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
        />
        {isErrorDiscount && (
          <Alert severity="error">{t('invalidDiscountCode')}</Alert>
        )}
        <Button
          variant="bordered"
          onClick={() => applyDiscount(discountCode)}
          disabled={!discountCode || isLoadingDiscount || isLoadingIntent}
          loading={isLoadingDiscount || isLoadingIntent}
        >
          {t('apply')}
        </Button>
      </Stack>
    </Dialog>
  );
};
